import * as UiTypes from '../types/ui' // eslint-disable-line import/no-namespace
import {
    NinchatEvents,
    NinchatProperties,
} from '../api/types'
import {
    PayloadAction,
    createSlice,
} from '@reduxjs/toolkit'
import {
    setSlice,
    sliceFilters,
} from '../store/storage'
import {CannedMessageKey} from 'components/SendCannedMessages/types'
import {DateTime} from 'luxon'
import {MessageState} from '../components/Send/types'
import {NinchatActionType} from 'api/types/actions'
import {UiStore} from '../store/types'
import {addQueuePickupDelayReducers} from 'middlewares/ninchat/queuePickupDelayActions'
import {closeSession} from 'api/action/closeSession'
import {startAppListening} from '../middlewares/listener'
import {updateUser} from 'api/action/updateUser'

export interface UiState {
    canned_message_key?: CannedMessageKey
    file_uploads?: UiStore.UiAttachmentFileStore
    queue_pickup_delays?: {[key: NinchatProperties.QueueID]: boolean}
    message_state?: {[key: NinchatProperties.ChatID]: Partial<MessageState>}
    realm_id?: NinchatProperties.RealmID
    audience_realm_id?: NinchatProperties.RealmID
    show_channel_sidebar?: boolean
    login_error?: {
        at?: number
        error_type?: string
    }
    isModalOpen?: boolean
}

const initialState: UiState = {isModalOpen: false}

sliceFilters.set('ui', {
    canned_message_key: true,
    realm_id: true,
    show_channel_sidebar: true,
})

type ClearFileUploadActionPayload = {
    chat_id: NinchatProperties.ChatID;
    filename: UiTypes.FileName;
}

type SendFileActionPayload = {
    actionType?: NinchatActionType.NinchatSendFileUnlinkedAction
    chat: NinchatProperties.Chat;
    files: UiStore.UiStoreAttachmentFile[];
}

type UpdateCannedMessageKeyActionPayload = CannedMessageKey

type UpdateShowChannelSidebarActionPayload = boolean

type UpdateFileUploadActionPayload = {
    chat_id: NinchatProperties.ChatID;
    data: {
        error?: NinchatEvents.NinchatErrorEvent,
        progress?: UiTypes.Count,
        file_id?: NinchatProperties.FileID,
    };
    filename: UiTypes.FileName;
}
export const fileQueue = new Map()

export const uiSlice = createSlice({
    extraReducers: (builder) => addQueuePickupDelayReducers(builder)
        .addCase(closeSession.pending, (state) => ({
            ...initialState,
            ...state.login_error && {
                login_error: {
                    ...state.login_error,
                    at: undefined,
                },
            },
        })),
    initialState,
    name: 'UI',
    reducers: {
        clearFileUpload: (state, action: PayloadAction<ClearFileUploadActionPayload>) => {
            const {
                chat_id,
                filename,
            } = action.payload

            state.file_uploads ??= {}
            state.file_uploads[chat_id] = (state.file_uploads[chat_id] ?? []).filter((file) => file.name !== filename)
        },
        clearMessageState: (state, action: PayloadAction<NinchatProperties.Chat>) => {
            const id = action.payload.channel_id ?? action.payload.user_id

            delete state?.message_state?.[id]

            if (!Object.keys(state?.message_state ?? {}).length) {
                delete state.message_state
            }
        },
        saveMessageState: (state, action: PayloadAction<Partial<MessageState>>) => {
            const {chat} = action.payload

            if (!chat) {
                return
            }

            const id = chat.channel_id ?? chat.user_id

            state.message_state ??= {}
            state.message_state[id] = action.payload
        },
        sendFile: (state, action: PayloadAction<SendFileActionPayload>) => {
            const {
                chat,
                files,
            } = action.payload

            const chat_id = chat?.channel_id ?? chat?.user_id as NinchatProperties.ChatID

            state.file_uploads ??= {}
            state.file_uploads[chat_id] ??= []
            state.file_uploads[chat_id].push(...files)
        },
        toggleModalState: (state, action: PayloadAction<boolean>) => {
            state.isModalOpen = action.payload
        },
        updateAudienceRealm: (state, action) => {
            state.audience_realm_id = action.payload
        },
        updateCannedMessageKey: (state, action: PayloadAction<UpdateCannedMessageKeyActionPayload>) => {
            state.canned_message_key = action.payload
        },
        updateFileUpload: (state, action: PayloadAction<UpdateFileUploadActionPayload>) => {
            const {
                chat_id,
                data,
                filename,
            } = action.payload

            state.file_uploads ??= {}
            state.file_uploads[chat_id] = (state.file_uploads[chat_id] ?? []).map((file) => (file.name === filename ?
                {
                    ...file,
                    ...data,
                }
                : file
            ))
        },
        updateLoginErrorType: (state, action: PayloadAction<string|undefined>) => {
            state.login_error = {
                at: DateTime.now().toMillis(),
                error_type: action.payload,
            }
        },
        updateRealm: (state, action) => {
            state.realm_id = action.payload
        },
        updateShowChannelSidebar: (state, action: PayloadAction<UpdateShowChannelSidebarActionPayload>) => {
            state.show_channel_sidebar = action.payload
        },
    },
})

export const {reducer: uiReducer} = uiSlice

export const {
    clearMessageState,
    saveMessageState,
    toggleModalState,
    updateCannedMessageKey,
    updateFileUpload,
    updateAudienceRealm,
    updateRealm,
    updateShowChannelSidebar,
    updateLoginErrorType,
} = uiSlice.actions

startAppListening({
    effect: (_action, listenerApi) => setSlice({ui: listenerApi.getState().ui}),
    predicate: (action) => typeof action === 'object' && action.type.startsWith('UI/'),
})

startAppListening({
    actionCreator: updateRealm,
    effect: (action, listenerApi) => {
        const realmId = action.payload
        if (!realmId) {
            return
        }
        listenerApi.dispatch(updateUser({user_settings: {realm_id: realmId}}))
    },
})

export const clearFileUpload = (payload: ClearFileUploadActionPayload): ReturnType<typeof uiSlice.actions.clearFileUpload> => {
    const {filename} = payload

    fileQueue.delete(filename)

    return uiSlice.actions.clearFileUpload(payload)
}
clearFileUpload.type = uiSlice.actions.clearFileUpload.type

export const sendFile = (payload: SendFileActionPayload): ReturnType<typeof uiSlice.actions.sendFile> => {
    const {files} = payload

    const storeFiles: UiStore.UiStoreAttachmentFile[] = []

    for (const file of files) {
        fileQueue.set(file.name, file)

        storeFiles.push((({
            name, size, thumbnail, type,
        }) => ({
            name,
            size,
            ...thumbnail && {thumbnail},
            type,
        }))(file))
    }
    payload.files = storeFiles

    return uiSlice.actions.sendFile(payload)
}
sendFile.type = uiSlice.actions.sendFile.type
